import { z } from "zod";

/**
 * A captured homepage image, as a reader sees it: where to fetch it, which
 * version it came from, and when it was taken.
 *
 * **One nullable object, not three nullable fields.** The database pairs
 * `screenshotObjectKey` and `screenshotCapturedAt` with a CHECK constraint
 * (`(screenshotObjectKey IS NULL) = (screenshotCapturedAt IS NULL)`) because a
 * capture instant with nothing captured — or an image with no instant — is
 * indistinguishable from a state someone forgot to clear. Grouping them here
 * makes that half-set state unrepresentable in the contract rather than merely
 * unlikely: a consumer cannot read a `url` without also having been handed the
 * version and the instant that qualify it. Same shape, and the same reason, as
 * `VibeAppIconSchema` and as `VibeAppScreenshot` in `VibeAppThumbnail.tsx`.
 *
 * `versionNumber` rides along rather than being read off the app because the
 * whole point of storing the image on the DEPLOYMENT is that it can never be
 * attributed to a version other than the one that produced it. Reading it from
 * the app row at render time would reintroduce exactly the drift the column
 * placement was chosen to make structurally impossible.
 *
 * `url` is resolved server-side from the stored object key. The key itself
 * never crosses the API boundary — it is a bucket-layout detail, and a client
 * that learned it would be one refactor away from composing its own URLs.
 */
export const VibeDeploymentScreenshotSchema = z.object({
  url: z.string().url(),
  /// The version the image was captured FROM — never the newest attempt.
  versionNumber: z.number().int(),
  /// The capture instant, not the deploy instant. This is what makes a stale
  /// image unable to imply a fresh deploy.
  capturedAt: z.coerce.date()
});
export type VibeDeploymentScreenshot = z.infer<typeof VibeDeploymentScreenshotSchema>;

/**
 * Viewport the capture browser reports to the page, in CSS pixels.
 *
 * 1280×800 is a desktop breakpoint every responsive homepage has a layout for,
 * and it is wide enough that a site's nav renders in its full form rather than
 * collapsed to a hamburger — which is what makes one app recognisable from the
 * next in a grid of thumbnails.
 *
 * The console crops this hard: the card's band is 4:1 with `object-top`, so
 * roughly the first 320px of the page is what anyone actually sees. The rest is
 * captured anyway because the detail surfaces may want more of it later, and
 * re-capturing the fleet to change a crop is more expensive than storing a few
 * extra kilobytes now.
 */
export const VIBE_SCREENSHOT_VIEWPORT_WIDTH_PX = 1280;
export const VIBE_SCREENSHOT_VIEWPORT_HEIGHT_PX = 800;

/**
 * Device scale factor for the capture.
 *
 * 1, not 2. A retina capture doubles both axes — four times the pixels and
 * roughly four times the stored bytes — to serve a band that is ~200 CSS pixels
 * wide on the card. The image is already downscaled by more than 4× at every
 * placement, so the extra sampling is spent and then thrown away.
 */
export const VIBE_SCREENSHOT_DEVICE_SCALE_FACTOR = 1;

/**
 * How long the capture waits for a page before giving up, in milliseconds.
 *
 * Generous, because the subject is a freshly-deployed app: a cold container
 * answering its first request can take several seconds before it renders
 * anything. Bounded, because this runs inside a cron tick that must finish —
 * a hung page holds a pooled Chromium page open, and the pool is shared with
 * PDF rendering.
 */
export const VIBE_SCREENSHOT_NAVIGATION_TIMEOUT_MS = 20_000;

/**
 * Ceiling on the DECODED pixel count the re-encoder will accept.
 *
 * Independent of any byte ceiling, and it is this one that stops a
 * decompression bomb — compression is exactly what such an image hides behind,
 * so a byte check cannot see it. The bytes here come from our own Chromium
 * rather than from a stranger, so this is defence in depth rather than the
 * primary guard; it costs nothing and it means a future change that lets an
 * app-supplied image reach this path does not silently arrive unguarded.
 */
export const VIBE_SCREENSHOT_MAX_DECODED_PIXELS = 4096 * 4096;

/**
 * WebP quality for the stored capture.
 *
 * A screenshot is a photograph of a UI: large flat regions with sharp text.
 * WebP at 80 holds text legibly at the sizes the console renders and lands a
 * 1280×800 page in tens of kilobytes, where the equivalent PNG is commonly
 * ten times that. Every browser the console supports decodes WebP.
 */
export const VIBE_SCREENSHOT_WEBP_QUALITY = 80;

/**
 * How many failed capture attempts a deployment gets before the sweep stops
 * offering it a browser.
 *
 * This is the whole reason `screenshotAttemptCount` exists. Vibe has no queue
 * and no event bus, so capture is a periodic sweep over deployments with no
 * screenshot — and without a give-up threshold that sweep relaunches a headless
 * browser against every permanently uncapturable app on every tick, forever.
 * The ledger is what makes the tick CONVERGE.
 *
 * Five, because the failures worth retrying are transient (a container still
 * warming, a DNS record still propagating, an edge certificate still issuing)
 * and all of them resolve well inside five spaced attempts. A sixth attempt on
 * an app that has failed five times spaced hours apart is not waiting on a
 * cold start; it is an app that cannot be captured.
 */
export const VIBE_SCREENSHOT_MAX_ATTEMPTS = 5;

/**
 * Minimum gap between two capture attempts on the same deployment, in minutes.
 *
 * Spacing, not a backoff curve: with a hard attempt ceiling of 5, an
 * exponential curve buys a handful of minutes of headroom and costs a column
 * to store the exponent in. A flat 30 minutes over five attempts gives a slow
 * app roughly two hours to come up, which is far longer than any cold start
 * this platform produces.
 */
export const VIBE_SCREENSHOT_RETRY_SPACING_MINUTES = 30;

/**
 * How many deployments one sweep tick will attempt.
 *
 * Each attempt drives a real page load in a shared Chromium, so an unbounded
 * tick over a large fleet would hold the pool for the whole sweep and starve
 * PDF rendering behind it. Capped work per tick with a frequent schedule
 * converges just as fast and keeps any single tick short.
 */
export const VIBE_SCREENSHOT_SWEEP_BATCH_SIZE = 10;
